import React from "react";
import { useRouter } from "next/router";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      isComponentError: false,
    };
  }

  static getDerivedStateFromError(error) {
    // Capture error immediately for initial render
    const isComponentError = error.message.includes("Element type is invalid");

    return {
      hasError: true,
      isComponentError,
    };
  }

  componentDidCatch(error, errorInfo) {
    const isComponentError = error.message.includes("Element type is invalid");

    // Send to error reporting service
    if (process.env.NODE_ENV === "production") {
      this.logErrorToService(error, errorInfo);
    }

    this.setState({
      error,
      errorInfo,
      isComponentError,
    });
  }

  logErrorToService = (error, errorInfo) => {
    // Implement actual error reporting (Sentry, LogRocket, etc.)
    console.error("Production Error:", error, errorInfo.componentStack);
  };

  handleReset = () => {
    if (this.state.isComponentError) {
      // Clear cache and hard reload for component import issues
      if ("caches" in window) {
        caches.keys().then((names) => {
          names.forEach((name) => caches.delete(name));
        });
      }
      window.location.reload();
    } else {
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
        isComponentError: false,
      });
    }
  };

  renderErrorDetails() {
    if (!this.state.error) return null;

    return (
      <div className="mt-6 p-4 bg-red-50 rounded text-sm overflow-auto">
        <p className="font-semibold mb-2">Error Details:</p>

        {this.state.isComponentError && (
          <div className="mb-3 p-3 bg-yellow-50 border-l-4 border-yellow-400">
            <p className="font-medium">Component Import Issue Detected</p>
            <p className="mt-1">This usually means:</p>
            <ul className="list-disc pl-5 mt-1">
              <li>You forgot to export a component from its file</li>
              <li>Mixed up default and named imports</li>
              <li>Circular dependency between components</li>
            </ul>
          </div>
        )}

        <p className="mb-2">{this.state.error.toString()}</p>

        {this.state.errorInfo && (
          <pre className="text-xs overflow-x-auto">
            {this.state.errorInfo.componentStack}
          </pre>
        )}
      </div>
    );
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
          <div className="bg-white shadow-lg rounded-lg p-8 max-w-md w-full">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              {this.state.isComponentError
                ? "Component Loading Error"
                : "Something went wrong"}
            </h2>

            <p className="text-gray-600 mb-6">
              {this.state.isComponentError
                ? "There was a problem loading a component. This is often caused by import/export issues."
                : "We apologize for the inconvenience. Our team has been notified of this issue."}
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={this.handleReset}
                className="bg-wine text-white px-4 py-2 rounded hover:bg-opacity-90"
              >
                {this.state.isComponentError
                  ? "Reload Application"
                  : "Try Again"}
              </button>

              <button
                onClick={() => (window.location.href = "/")}
                className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300"
              >
                Return to Home
              </button>
            </div>

            {(process.env.NODE_ENV !== "production" ||
              this.state.isComponentError) &&
              this.renderErrorDetails()}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
